import { useRef, useState, useLayoutEffect } from 'react'
import { cn } from '@shared/utils/cn'
import { BannerView } from '@shared/units/BannerView'
import { TitleView } from '@shared/units/TitleView'
import { TextView } from '@shared/units/TextView'
import { ImageView } from '@shared/units/ImageView'
import { GalleryMosaicView } from '@shared/units/GalleryMosaicView'
import { SilhouetteCell } from '@shared/units/SilhouetteCell'
import { Lightbox } from '@shared/units/Lightbox'
import { calcPositions as calcMosaicPositions } from '@shared/units/galleryMosaicPositions'
import template2Image1 from '@/assets/images/transmission_template/template2_image1.png'
import template2Image2 from '@/assets/images/transmission_template/template2_image2.png'

/**
 * Pure display layer for 中台心傳 (Transmission) pages.
 * No editing UI, no admin-only dependencies. Safe to copy to frontend.
 *
 * TransmissionLayout 是共用版面骨架（間距、寬度比例、overhang 等數字只在這裡寫一次）。
 * TransmissionView 用 *View 唯讀元件包裝它；TransmissionEditor.jsx 用 *Unit 可編輯元件
 * 包裝它（傳入 renderBlock），兩邊版面數字保持同步、不用各改一次。
 */

export const THEME_PRESETS = [
  { id: 0, color: '#EDEAE1', label: '米色' },
  { id: 1, color: '#F4EBEB', label: '玫瑰' },
  { id: 2, color: '#EBF3F4', label: '天青' },
  { id: 3, color: '#EEE7F6', label: '淡紫' },
  { id: 4, color: '#F3E2E6', label: '粉紅' },
  { id: 5, color: '#E6E7F6', label: '藍紫' },
  { id: 6, color: '#DDE6D7', label: '淡綠' },
]

export const DEFAULT_THEME = { colorId: 0 }

export function getThemeColor(colorId) {
  return THEME_PRESETS.find(t => t.id === colorId)?.color ?? THEME_PRESETS[0].color
}

export const SILHOUETTE_VARIANTS = [
  { id: 1, label: '交錯排列', layout: [
    { ml: 'auto', mr: 'auto' },
    { ml: '0',    mr: 'auto' },
    { ml: 'auto', mr: '0'    },
    { ml: '30%',  mr: '0'    },
  ]},
  { id: 2, label: '全靠左', layout: [
    { ml: '0', mr: 'auto' },
    { ml: '0', mr: 'auto' },
    { ml: '0', mr: 'auto' },
    { ml: '0', mr: 'auto' },
  ]},
  { id: 3, label: '全靠右', layout: [
    { ml: 'auto', mr: '0' },
    { ml: 'auto', mr: '0' },
    { ml: 'auto', mr: '0' },
    { ml: 'auto', mr: '0' },
  ]},
  { id: 4, label: '全置中', layout: [
    { ml: 'auto', mr: 'auto' },
    { ml: 'auto', mr: 'auto' },
    { ml: 'auto', mr: 'auto' },
    { ml: 'auto', mr: 'auto' },
  ]},
  { id: 5, label: '左右交替', layout: [
    { ml: '0',    mr: 'auto' },
    { ml: 'auto', mr: '0'    },
    { ml: '0',    mr: 'auto' },
    { ml: 'auto', mr: '0'    },
  ]},
  { id: 6, label: '非對稱錯位', layout: null },
  { id: 8, label: '圖文並排靠右', layout: null },
  { id: 9, label: '三欄+靠右', layout: null },
  { id: 10, label: '三欄裝飾錯位', layout: null },
  { id: 7, label: '交錯排列（鏡像）', layout: [
    { ml: '5%',   mr: 'auto' },
    { ml: 'auto', mr: '0'    },
    { ml: '0',    mr: 'auto' },
    { ml: 'auto', mr: '10%'  },
  ]},
]

const DEFAULT_TEMPLATE_SETTINGS = {
  intro: { gradient: true },
  modelImagePosition: 'left',
  modelTitleStyle: 'left',
  modelBg: 'color',
  modelSubtitleAlign: 'left',
  artifactImagePosition: 'right',
  artifactTitleStyle: 'left',
  artifactBg: 'color',
  silhouetteVariant: 1,
}

function ViewBlockContent({ block, isMobile, themeColor, noAspect, mobilePad, onOpenLightbox }) {
  if (!block) return null
  switch (block.type) {
    case 'banner':
      return <BannerView desktop={block.data.desktop} mobile={block.data.mobile} isMobile={isMobile} noAspect onOpenLightbox={onOpenLightbox} />
    case 'title':
      return <TitleView value={block.data.value} isMobile={isMobile} textColor="inherit" bold={false} mobileSizeClass="text-xl @min-[443px]:text-2xl" desktopSizeClass="text-3xl" mobilePad={mobilePad} />
    case 'text':
      return <TextView value={block.data.value} isMobile={isMobile} />
    case 'image':
      return <ImageView url={block.data.url} fullUrl={block.data.fullUrl} caption={block.data.caption} noAspect={noAspect} onOpenLightbox={onOpenLightbox} />
    case 'gallery-mosaic':
      return <GalleryMosaicView data={block.data} emptyColor={themeColor} onOpenLightbox={onOpenLightbox} />
    default:
      return null
  }
}

// 唯讀模式（TransmissionView）用：依畫面顯示順序，蒐集整篇心傳所有圖片的網址，
// 讓 Lightbox 能在 banner / 袖珍模型 / 九宮格 / 代表物件 / 精舍剪影 之間左右滑動切換，
// 而不是每個區塊各自開獨立的放大檢視。直接從 blocks 資料算，不依賴渲染時序。
function collectGalleryImages(blocks) {
  const urls = []
  const push = (id) => {
    const block = blocks.find(b => b.id === id)
    const url = block?.data?.fullUrl || block?.data?.url
    if (url) urls.push(url)
  }

  push('uuid-banner-1')
  push('uuid-model-image-1')

  const mosaicBlock = blocks.find(b => b.id === 'uuid-gallery-mosaic-1')
  const mosaicItems = mosaicBlock?.data?.items ?? []
  if (mosaicItems.length > 0) {
    calcMosaicPositions(mosaicItems).forEach(item => {
      const url = item.fullUrl || item.url
      if (url) urls.push(url)
    })
  }

  push('uuid-artifact-image-1')

  for (let n = 1; n <= 10; n++) push(`uuid-silhouette-image-${n}`)

  return urls
}

// 袖珍模型 / 代表物件區的圖片寬度比例（手機版 / 電腦版）
export const TRANSMISSION_IMG_WIDTH = {
  model: { mobile: '60%', desktop: '60%' },
  artifact: { mobile: '35%', desktop: '25%' },
}
// 袖珍模型 / 代表物件內文固定寬度（不撐滿剩餘空間），跟圖片置中、貼在一起，兩側留白
const MODEL_TEXT_WIDTH = '40%'
const ARTIFACT_TEXT_WIDTH = { mobile: '60%', desktop: '50%' }

// 用實際 DOM 量測畫垂直連接線：containerRef 是共同的 position:relative 祖先，
// fromRef 量測其「底部」當線的起點，toRef 量測其「頂部」當線的終點。
// 純 CSS 無法讓一條線同時滿足「固定水平位置」又「動態追蹤另一個元素因 flex 置中而變動的位置」，
// 純算 offset 每次圖片高度一變就會跑掉，改用量測才能保證任何內容高度下都準確連上。
function MeasuredConnector({ containerRef, fromRef, toRef, extraRefs = [], side, offset, alignRef, align = 'center', alignOffset = 0, color = '#1B1A1E', gapTop = 0, gapBottom = 0, watch }) {
  const [line, setLine] = useState(null)

  useLayoutEffect(() => {
    const update = () => {
      const container = containerRef.current
      const from = fromRef.current
      const to = toRef.current
      if (!container || !from || !to) return
      const containerRect = container.getBoundingClientRect()
      const fromRect = from.getBoundingClientRect()
      const toRect = to.getBoundingClientRect()
      // toRef 自己可能有上內距（例如文字欄的 py-6/py-8），實際可見內容要再往下才開始，
      // 用 computed style 量出這段內距，線才會停在真正看得到的文字邊緣，而不是空白的 padding 裡
      const toPaddingTop = parseFloat(getComputedStyle(to).paddingTop) || 0
      const top = fromRect.bottom - containerRect.top + gapTop
      const height = (toRect.top + toPaddingTop) - fromRect.bottom - gapTop - gapBottom
      // alignRef 提供時，水平位置改成量測該元素的實際邊緣/中心，而不是用固定 side/offset 百分比
      let left = null
      if (alignRef?.current) {
        const alignRect = alignRef.current.getBoundingClientRect()
        left = (align === 'left' ? alignRect.left : alignRect.left + alignRect.width / 2 - 1) - containerRect.left + alignOffset
      }
      setLine({ top, height: Math.max(0, height), left })
    }
    update()
    const ro = new ResizeObserver(update)
    if (containerRef.current) ro.observe(containerRef.current)
    if (fromRef.current) ro.observe(fromRef.current)
    if (toRef.current) ro.observe(toRef.current)
    // 圖片新增/移除時，內文本身的尺寸不一定變化（只是被擠動位置），
    // 但圖片所在的列容器一定會變高/變矮，額外監聽它才能即時重新畫線
    extraRefs.forEach(r => { if (r.current) ro.observe(r.current) })
    if (alignRef?.current) ro.observe(alignRef.current)
    window.addEventListener('resize', update)
    return () => { ro.disconnect(); window.removeEventListener('resize', update) }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [containerRef, fromRef, toRef, alignRef, align, alignOffset, gapTop, gapBottom, watch])

  if (!line || line.height <= 0) return null
  const horizontal = line.left != null ? { left: line.left } : { [side]: offset }
  return <div style={{ position: 'absolute', ...horizontal, top: line.top, height: line.height, width: '2px', background: color, zIndex: 0, pointerEvents: 'none' }} />
}

// 袖珍模型圖片用 position:absolute 溢出置中，不佔版面空間；但圖片本身不能縮小，
// 高度不可控，太高時上緣會蓋到上方標題、下緣會蓋到下一個區塊（九宮格）。
// 用實際 DOM 量測 beforeRef 底部跟 afterRef 頂部的間距，不夠就動態加大留白，
// 剛好夠用、不多留，圖片換多高都自動補足，不用手動調數字。
function useVerticalClearance(beforeRef, afterRef, minGap = 8, watch) {
  const [extraGap, setExtraGap] = useState(0)
  const extraGapRef = useRef(0)

  useLayoutEffect(() => {
    const update = () => {
      const before = beforeRef.current
      const after = afterRef.current
      if (!before || !after) return
      const beforeRect = before.getBoundingClientRect()
      const afterRect = after.getBoundingClientRect()
      const currentGap = afterRect.top - beforeRect.bottom
      // 扣掉目前已套用的 extraGap，還原成「沒有任何補償時」的原始間距，
      // 才能算出正確的絕對需求值——用累加只會越補越多、內容變矮時也縮不回來
      const intrinsicGap = currentGap - extraGapRef.current
      const required = Math.max(0, minGap - intrinsicGap)
      if (Math.abs(required - extraGapRef.current) > 0.5) {
        extraGapRef.current = required
        setExtraGap(required)
      }
    }
    update()
    // 首次掛載時（例如切到預覽 overlay 當下新建的元件樹）圖片可能還沒完成 decode/paint，
    // 這一輪量到的高度不準；補一個下一幀重新量測，確保掛載後有一次「畫面已定」的量測結果
    const raf = requestAnimationFrame(update)
    const ro = new ResizeObserver(update)
    if (beforeRef.current) ro.observe(beforeRef.current)
    if (afterRef.current) ro.observe(afterRef.current)
    window.addEventListener('resize', update)
    return () => { cancelAnimationFrame(raf); ro.disconnect(); window.removeEventListener('resize', update) }
    // watch 用來讓「串接在後面」的量測，在前一段量測更新後跟著重新驗證一次
  }, [beforeRef, afterRef, minGap, watch])

  return extraGap
}

// 保底用：確保 containerRef 的高度一定包住 contentRef（例如溢出的圖片），
// 不管 contentRef 後面還有沒有其他區塊可以量測間距（例如精舍剪影全空被隱藏時），
// 都能讓外層容器自動撐高，背後的底色才不會在圖片中途被截斷
function useContainerBottomPadding(containerRef, contentRef, minGap = 0) {
  const [padding, setPadding] = useState(0)
  const paddingRef = useRef(0)

  useLayoutEffect(() => {
    const update = () => {
      const container = containerRef.current
      const content = contentRef.current
      if (!container || !content) return
      const containerRect = container.getBoundingClientRect()
      const contentRect = content.getBoundingClientRect()
      // 扣掉目前已套用的 padding，還原成容器原本（未補償）的底邊位置
      const intrinsicBottom = containerRect.bottom - paddingRef.current
      const overflow = contentRect.bottom - intrinsicBottom
      const required = Math.max(minGap, overflow)
      if (Math.abs(required - paddingRef.current) > 0.5) {
        paddingRef.current = required
        setPadding(required)
      }
    }
    update()
    const raf = requestAnimationFrame(update)
    const ro = new ResizeObserver(update)
    if (containerRef.current) ro.observe(containerRef.current)
    if (contentRef.current) ro.observe(contentRef.current)
    window.addEventListener('resize', update)
    return () => { cancelAnimationFrame(raf); ro.disconnect(); window.removeEventListener('resize', update) }
  }, [containerRef, contentRef, minGap])

  return padding
}

// 用 transform 把 selfRef 的視覺中心對齊到 targetRef 的中心，不佔用/不改變版面原本的流動空間
function CenterOverTarget({ selfRef, targetRef, children, watch, align = 'center' }) {
  const [shift, setShift] = useState(0)
  const shiftRef = useRef(0)

  useLayoutEffect(() => {
    const update = () => {
      const self = selfRef.current
      const target = targetRef.current
      if (!self || !target) return
      const selfRect = self.getBoundingClientRect()
      const targetRect = target.getBoundingClientRect()
      // selfRect 量到的是「已經套用上次 shift」後的位置，要先扣掉才是未位移的原始座標，
      // 不然每次重新量測都會把上次的位移疊加進去，越切換越偏
      const selfLeft = selfRect.left - shiftRef.current
      const selfAnchor = align === 'left' ? selfLeft : selfLeft + selfRect.width / 2
      const targetAnchor = align === 'left' ? targetRect.left : targetRect.left + targetRect.width / 2
      const newShift = targetAnchor - selfAnchor
      shiftRef.current = newShift
      setShift(newShift)
    }
    update()
    const ro = new ResizeObserver(update)
    if (selfRef.current) ro.observe(selfRef.current)
    if (targetRef.current) ro.observe(targetRef.current)
    window.addEventListener('resize', update)
    return () => { ro.disconnect(); window.removeEventListener('resize', update) }
  }, [selfRef, targetRef, watch, align])

  // display:inline-block 讓外框收縮成文字實際寬度，量出來的「中心」才是文字本身的視覺中心，
  // 不會是滿寬區塊（<p> 預設撐滿容器）的中心
  return <div ref={selfRef} style={{ display: 'inline-block', transform: `translateX(${shift}px)` }}>{children}</div>
}

// 固定高度的區塊（保證佔用實際版面空間、一定看得到），水平位置改用量測 alignRef 動態對齊，
// 不像 MeasuredConnector 那樣依賴量測「上下兩點間的距離」來決定高度（那個距離在 margin collapse 等情況下可能算出 0）
function AlignedFixedConnector({ containerRef, alignRef, align = 'left', height = 64, color = '#1B1A1E', watch }) {
  const [left, setLeft] = useState(null)

  useLayoutEffect(() => {
    const update = () => {
      const container = containerRef.current
      const target = alignRef.current
      if (!container || !target) return
      const containerRect = container.getBoundingClientRect()
      const targetRect = target.getBoundingClientRect()
      const x = align === 'left' ? targetRect.left : targetRect.left + targetRect.width / 2 - 1
      setLeft(x - containerRect.left)
    }
    update()
    const ro = new ResizeObserver(update)
    if (containerRef.current) ro.observe(containerRef.current)
    if (alignRef.current) ro.observe(alignRef.current)
    window.addEventListener('resize', update)
    return () => { ro.disconnect(); window.removeEventListener('resize', update) }
  }, [containerRef, alignRef, align, watch])

  if (left == null) return null
  return (
    <div className="relative" style={{ height }}>
      <div style={{ position: 'absolute', left, top: 4, bottom: 4, width: '2px', background: color }} />
    </div>
  )
}

export function SectionTitle({ value, titleStyle, isMobile, barColor }) {
  const base = cn('font-semibold font-serif tracking-wide text-[#1B1A1E] whitespace-pre-line', isMobile ? 'text-2xl @min-[443px]:text-3xl' : 'text-4xl')
  const bar = (margin) => <div className="mt-2" style={{ width: '95px', height: '9px', background: barColor, margin }} />
  // 色塊用負 margin 抵銷外層 section 的 px-6（1.5rem），貼齊區塊最左/右邊，跟 underline 樣式的效果一致
  const barFlushLeft = () => bar('0.5rem 0 0 -1.5rem')
  const barFlushRight = () => bar('0.5rem -1.5rem 0 auto')

  if (titleStyle === 'center') return barColor ? (
    <div>
      <p className={cn(base, 'text-center')}>{value}</p>
      {bar('0.5rem auto 0')}
    </div>
  ) : <p className={cn(base, 'text-center')}>{value}</p>

  if (titleStyle === 'right') return barColor ? (
    <div>
      <p className={cn(base, 'text-right')}>{value}</p>
      {barFlushRight()}
    </div>
  ) : <p className={cn(base, 'text-right')}>{value}</p>

  if (titleStyle === 'left-line') {
    const row = (
      <div className="flex items-center gap-4">
        <p className={cn(base, 'shrink-0')}>{value}</p>
        <span className="flex-1 border-t border-foreground/20" />
      </div>
    )
    return barColor ? <div>{row}{barFlushLeft()}</div> : row
  }
  if (titleStyle === 'right-line') {
    const row = (
      <div className="flex items-center gap-4">
        <span className="flex-1 border-t border-foreground/20" />
        <p className={cn(base, 'shrink-0')}>{value}</p>
      </div>
    )
    return barColor ? <div>{row}{barFlushRight()}</div> : row
  }
  if (titleStyle === 'line-right') return (
    <div className="flex items-end">
      <p className={cn(base, 'shrink-0')}>{value}</p>
      <span className="flex-1 border-b border-black" style={{ marginBottom: '0.4em' }} />
    </div>
  )
  if (titleStyle === 'line-left') return (
    <div className="flex items-end">
      <span className="flex-1 border-b border-black" style={{ marginBottom: '0.4em' }} />
      <p className={cn(base, 'shrink-0')}>{value}</p>
    </div>
  )
  if (titleStyle === 'underline-left' || titleStyle === 'underline-right') return (
    <div style={{ textAlign: titleStyle === 'underline-right' ? 'right' : 'left', paddingLeft: titleStyle === 'underline-right' ? '1.5rem' : 0, paddingRight: titleStyle === 'underline-right' ? 0 : '1.5rem' }}>
      <p className={base} style={{ paddingLeft: titleStyle === 'underline-left' ? '1.5rem' : 0, paddingRight: titleStyle === 'underline-right' ? '1.5rem' : 0 }}>{value}</p>
      <div className="relative mt-2" style={{ width: '95px', height: '9px', background: barColor ?? '#72965A', marginLeft: titleStyle === 'underline-right' ? 'auto' : 0, zIndex: 1 }} />
      <div style={{ marginTop: '-1px', borderTop: '1px solid black' }} />
    </div>
  )
  return barColor ? (
    <div>
      <p className={base}>{value}</p>
      {barFlushLeft()}
    </div>
  ) : <p className={base}>{value}</p>
}

/**
 * 共用版面骨架：間距、寬度比例、overhang 等數字只在這裡寫一次。
 * renderBlock(id, extra) 決定 leaf 要渲染 *View（唯讀）還是 *Unit（可編輯）元件，
 * 預設用唯讀的 ViewBlockContent。extraSectionProps(sectionKey) 讓呼叫端（如編輯器的版型側欄）
 * 對特定區塊額外加 props（例如 isLayoutMode）。
 */
export function TransmissionLayout({
  blocks, theme = DEFAULT_THEME, settings = DEFAULT_TEMPLATE_SETTINGS, isMobile,
  renderBlock, extraBlockProps, isEditing = false,
}) {
  const themeColor = getThemeColor(theme.colorId)
  const renderer = renderBlock ?? ((props) => <ViewBlockContent {...props} />)

  // 唯讀模式（renderBlock 未提供，即前端 TransmissionView）：整篇圖片共用同一個可左右滑動的 Lightbox；
  // 編輯模式（TransmissionEditor 傳入自訂 renderBlock）點圖是要換圖/裁切，不套用這套邏輯。
  const isReadOnlyView = !renderBlock
  const [lightboxIndex, setLightboxIndex] = useState(null)
  const galleryImages = isReadOnlyView ? collectGalleryImages(blocks) : []
  const openLightbox = (url) => {
    const idx = galleryImages.indexOf(url)
    if (idx >= 0) setLightboxIndex(idx)
  }

  const bp = (id, extra = {}) => ({ block: blocks.find(b => b.id === id), isMobile, themeColor, mobilePad: id === 'uuid-title-1' ? 'px-3' : '', onOpenLightbox: isReadOnlyView ? openLightbox : undefined, ...extraBlockProps?.(id), ...extra })

  // 預覽時（非編輯模式）若一個區塊的所有內容都是空的，整塊隱藏、不留空白；
  // 固定的區塊大標題（如「袖珍模型」）不算內容，只看實際可編輯的圖片/文字/小標題
  const isBlockContentEmpty = (id) => {
    const block = blocks.find(b => b.id === id)
    if (!block) return true
    if (block.type === 'image') return !block.data?.url
    return !block.data?.value || block.data.value.replace(/<[^>]*>/g, '').trim() === ''
  }
  const isSectionEmpty = (...ids) => !isEditing && ids.every(isBlockContentEmpty)
  const isSilhouetteEmpty = !isEditing && blocks.every(b => !b.id.startsWith('uuid-silhouette-') || isBlockContentEmpty(b.id))

  // 模板七（silhouetteVariant: 10）的底線標題裝飾直接綁定 variant 推導，不存成獨立欄位：
  // silhouetteVariant 從模板七建立起就一直存在於每筆資料，改版即刻對舊資料生效，不需要重新存檔補欄位
  const isDecorVariant = settings.silhouetteVariant === 10
  // 模板二（silhouetteVariant: 6）標題色塊裝飾，同樣直接綁定 variant 推導
  const isTemplate2 = settings.silhouetteVariant === 6
  const titleBarColor = isTemplate2 ? '#CDA569' : undefined
  // 模板三（silhouetteVariant: 5）微調間距，同樣直接綁定 variant 推導
  const isTemplate3 = settings.silhouetteVariant === 5
  // 模板六（silhouetteVariant: 9）微調間距，同樣直接綁定 variant 推導
  const isTemplate6 = settings.silhouetteVariant === 9
  // 模板五（silhouetteVariant: 8）標題→內文的垂直連接線裝飾，同樣直接綁定 variant 推導
  const isTemplate5 = settings.silhouetteVariant === 8
  // 模板四（silhouetteVariant: 7）模仿模板五的連接線，同樣直接綁定 variant 推導
  const isTemplate4 = settings.silhouetteVariant === 7
  const showConnector = isTemplate5 || isTemplate4
  // 精舍剪影連接線：固定高度撐開標題跟下方內容的間距，讓線在寬鬆空間裡自然變長
  const verticalConnector = (side, offset, extraHeight = 0, extraStyle = {}, topInset = 4) => (
    <div className="relative" style={{ height: (isMobile ? 64 : 96) + extraHeight, ...extraStyle }}>
      <div style={{ position: 'absolute', [side]: offset, top: topInset, bottom: 4, width: '2px', background: '#1B1A1E' }} />
    </div>
  )
  // 袖珍模型/代表物件的連接線：圖片一撐高，內文因 items-center 置中位置就會動態改變，
  // 純 CSS offset 算不準，改用 ref + getBoundingClientRect 量測實際位置畫線
  const modelSectionRef = useRef(null)
  const modelSubtitleRef = useRef(null)
  const modelTextRef = useRef(null)
  const modelRowRef = useRef(null)
  const modelImageOverlayRef = useRef(null)
  const galleryMosaicRef = useRef(null)
  const modelImageClearanceTop = useVerticalClearance(modelSubtitleRef, modelImageOverlayRef)
  const modelImageClearanceBottom = useVerticalClearance(modelImageOverlayRef, galleryMosaicRef, 8, modelImageClearanceTop)
  const artifactSectionRef = useRef(null)
  const artifactSubtitleRef = useRef(null)
  const artifactTextRef = useRef(null)
  const artifactRowRef = useRef(null)
  const artifactTitle1Ref = useRef(null)
  const artifactImageOverlayRef = useRef(null)
  const artifactBeforeRowRef = useRef(null)
  const silhouetteSectionRef = useRef(null)
  const silhouetteTitleRef = useRef(null)
  const silhouetteFirstItemRef = useRef(null)
  const artifactImageClearanceTop = useVerticalClearance(artifactBeforeRowRef, artifactImageOverlayRef)
  const artifactImageClearanceBottom = useVerticalClearance(artifactImageOverlayRef, silhouetteSectionRef, 8, artifactImageClearanceTop)
  const pageWrapperRef = useRef(null)
  const pageBottomPadding = useContainerBottomPadding(pageWrapperRef, artifactImageOverlayRef)
  const modelImagePosition = settings.modelImagePosition ?? 'left'
  const modelTitleStyle = settings.modelTitleStyle ?? (isDecorVariant ? 'underline-left' : 'left')
  const modelBg = settings.modelBg ?? 'color'
  const modelSubtitleAlign = settings.modelSubtitleAlign ?? 'left'
  const artifactImagePosition = settings.artifactImagePosition ?? 'right'
  const artifactTitleStyle = settings.artifactTitleStyle ?? (isDecorVariant ? 'underline-right' : 'left')
  const artifactBg = settings.artifactBg ?? 'color'
  const silhouetteTitleStyle = settings.silhouetteTitleStyle ?? (isDecorVariant ? 'underline-left' : 'left')
  const silhouetteV = SILHOUETTE_VARIANTS.find(v => v.id === settings.silhouetteVariant) ?? SILHOUETTE_VARIANTS[0]

  return (
    <div ref={pageWrapperRef} className="@container w-full" style={{ paddingBottom: pageBottomPadding }}>

      {/* Banner */}
      {blocks.find(b => b.id === 'uuid-banner-1') && (
        renderer(bp('uuid-banner-1'))
      )}

      {/* 對聯標題 + 正文 */}
      <div className="relative z-10 w-full py-6 space-y-4" style={{
        paddingLeft: isMobile ? '1rem' : '40%',
        paddingRight: isMobile ? '1rem' : '4%',
        background: settings.intro?.gradient ? `linear-gradient(to bottom, #ffffff, ${themeColor})` : undefined,
      }}>
        {['uuid-title-1', 'uuid-content-1'].map(id => {
          const block = blocks.find(b => b.id === id)
          if (!block) return null
          return <div key={id}>{renderer(bp(id))}</div>
        })}
      </div>

      {/* ── 袖珍模型區 ── */}
      {blocks.find(b => b.id === 'uuid-section-1') && !isSectionEmpty('uuid-model-title-1', 'uuid-model-image-1', 'uuid-model-text-1') && (
        <div ref={modelSectionRef} className={cn('w-full py-10 relative', (modelTitleStyle === 'underline-left' || modelTitleStyle === 'underline-right') ? '' : 'px-6')}>
          <div className="relative z-10 mb-2">
            <SectionTitle value={blocks.find(b => b.id === 'uuid-section-1')?.data.value} titleStyle={modelTitleStyle} isMobile={isMobile} barColor={titleBarColor} />
          </div>
          <div ref={modelSubtitleRef} className="relative z-10" style={{ marginBottom: (showConnector ? (isMobile ? 36 : 80) : isTemplate2 ? (isMobile ? 24 : 64) : isTemplate3 ? (isMobile ? 20 : 48) : isTemplate6 ? (isMobile ? 20 : 48) : (isMobile ? 12 : 32)) + modelImageClearanceTop, paddingLeft: '5%', paddingRight: '5%', textAlign: modelSubtitleAlign }}>
            {renderer(bp('uuid-model-title-1'))}
          </div>
          {showConnector && (
            <MeasuredConnector containerRef={modelSectionRef} fromRef={modelSubtitleRef} toRef={modelTextRef} extraRefs={[modelRowRef]} alignRef={isTemplate4 ? modelTextRef : undefined} align="left" alignOffset={16} side="left" offset="10%" gapTop={12} gapBottom={8} watch={isMobile} />
          )}
          {(() => {
            const imgW = isMobile ? TRANSMISSION_IMG_WIDTH.model.mobile : TRANSMISSION_IMG_WIDTH.model.desktop
            const isLeft = modelImagePosition === 'left'
            const bleed = (modelTitleStyle === 'underline-left' || modelTitleStyle === 'underline-right') ? '0' : '-1.5rem'
            return (
              <div ref={modelRowRef} className="relative" style={{ marginLeft: bleed, marginRight: bleed, marginBottom: modelImageClearanceBottom }}>
                {isTemplate2 && (
                  <img src={template2Image1} alt="" className="absolute right-0 top-1/2 h-auto pointer-events-none select-none" style={{ width: '45%', transform: 'translateY(-50%)' }} />
                )}
                <div className="relative z-10 flex items-center justify-center text-[0.75rem] @min-[443px]:text-[0.875rem] @min-[768px]:text-base" style={{ background: (modelBg === 'color' && !isTemplate2) ? themeColor : undefined, minHeight: isMobile ? '8rem' : '20.8rem' }}>
                  {isLeft ? (
                    <>
                      <div className="shrink-0" style={{ width: imgW, paddingLeft: '1rem', paddingRight: '0.5rem' }}>
                        <div className="relative w-full">
                          <div ref={modelImageOverlayRef} className="absolute top-1/2 left-0 w-full z-20" style={{ transform: 'translateY(-50%)' }}>
                            {renderer(bp('uuid-model-image-1', { noAspect: true }))}
                          </div>
                        </div>
                      </div>
                      <div ref={modelTextRef} className="shrink-0 py-6 @min-[443px]:py-8" style={{ width: MODEL_TEXT_WIDTH, paddingRight: isMobile ? '1rem' : '1.5rem' }}>
                        {renderer(bp('uuid-model-text-1'))}
                      </div>
                    </>
                  ) : (
                    <>
                      <div ref={modelTextRef} className="shrink-0 py-6 @min-[443px]:py-8" style={{ width: MODEL_TEXT_WIDTH, paddingLeft: isMobile ? '1rem' : '1.5rem' }}>
                        {renderer(bp('uuid-model-text-1'))}
                      </div>
                      <div className="shrink-0" style={{ width: imgW, paddingLeft: '0.5rem', paddingRight: '1rem' }}>
                        <div className="relative w-full">
                          <div ref={modelImageOverlayRef} className="absolute top-1/2 left-0 w-full z-20" style={{ transform: 'translateY(-50%)' }}>
                            {renderer(bp('uuid-model-image-1', { noAspect: true }))}
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            )
          })()}
        </div>
      )}

      {/* 九宮格 */}
      {blocks.find(b => b.id === 'uuid-gallery-mosaic-1') && (
        <div ref={galleryMosaicRef} className="relative z-10 w-full px-4 py-6">
          {renderer(bp('uuid-gallery-mosaic-1'))}
        </div>
      )}

      {/* ── 代表物件區 ── */}
      {blocks.find(b => b.id === 'uuid-section-2') && !isSectionEmpty('uuid-artifact-title-1', 'uuid-artifact-image-1', 'uuid-artifact-text-1') && (
        <div ref={artifactSectionRef} className={cn('w-full py-10 relative', (artifactTitleStyle === 'underline-left' || artifactTitleStyle === 'underline-right') ? '' : 'px-6')}>
          <div ref={(!isDecorVariant && !isTemplate2 && !isTemplate6) ? artifactBeforeRowRef : undefined} className={cn('relative z-10', (isTemplate3 || showConnector || isTemplate6) ? 'mb-8' : 'mb-4')}>
            {showConnector ? (
              <CenterOverTarget selfRef={artifactSubtitleRef} targetRef={artifactTitle1Ref} watch={isMobile} align={isTemplate4 ? 'center' : 'left'}>
                <SectionTitle value={blocks.find(b => b.id === 'uuid-section-2')?.data.value} titleStyle={artifactTitleStyle} isMobile={isMobile} barColor={titleBarColor} />
              </CenterOverTarget>
            ) : (
              <div ref={artifactSubtitleRef}>
                <SectionTitle value={blocks.find(b => b.id === 'uuid-section-2')?.data.value} titleStyle={artifactTitleStyle} isMobile={isMobile} barColor={titleBarColor} />
              </div>
            )}
          </div>
          {isDecorVariant && (
            <div ref={artifactBeforeRowRef} className="relative z-10 mb-4 text-center" style={{ paddingLeft: '10%', paddingRight: '10%' }}>
              {renderer(bp('uuid-artifact-title-1'))}
            </div>
          )}
          {(isTemplate2 || isTemplate6) && (
            <div ref={artifactBeforeRowRef} className="relative z-10 mb-8">
              {renderer(bp('uuid-artifact-title-1'))}
            </div>
          )}
          {showConnector && (
            <MeasuredConnector containerRef={artifactSectionRef} fromRef={artifactSubtitleRef} toRef={artifactTextRef} extraRefs={[artifactRowRef]} alignRef={artifactTitle1Ref} align={isTemplate4 ? 'center' : 'left'} gapTop={12} gapBottom={8} watch={isMobile} />
          )}
          {(() => {
            const imgW = isMobile ? TRANSMISSION_IMG_WIDTH.artifact.mobile : TRANSMISSION_IMG_WIDTH.artifact.desktop
            const textW = isMobile ? ARTIFACT_TEXT_WIDTH.mobile : ARTIFACT_TEXT_WIDTH.desktop
            const isRight = artifactImagePosition === 'right'
            const bleed = (artifactTitleStyle === 'underline-left' || artifactTitleStyle === 'underline-right') ? '0' : '-1.5rem'
            return (
              <div ref={artifactRowRef} className="relative" style={{ marginLeft: bleed, marginRight: bleed, marginTop: artifactImageClearanceTop, marginBottom: artifactImageClearanceBottom }}>
                {isTemplate2 && (
                  <img src={template2Image2} alt="" className="absolute left-0 top-1/2 h-auto pointer-events-none select-none" style={{ width: '45%', transform: 'translateY(-50%)' }} />
                )}
                <div className="relative z-10 flex items-center justify-center text-[0.75rem] @min-[443px]:text-[0.875rem] @min-[768px]:text-base" style={{ background: (artifactBg === 'color' && !isTemplate2) ? themeColor : undefined, gap: isMobile ? 0 : '2rem', minHeight: isMobile ? '8rem' : '20.8rem' }}>
                  {isRight ? (
                    <>
                      <div ref={artifactTextRef} className="shrink-0 py-4 @min-[443px]:py-6" style={{ width: textW, paddingLeft: isMobile ? '1rem' : '1.5rem' }}>
                        {!isDecorVariant && !isTemplate2 && !isTemplate6 && (
                          <div ref={showConnector ? artifactTitle1Ref : undefined}>{renderer(bp('uuid-artifact-title-1'))}</div>
                        )}
                        {renderer(bp('uuid-artifact-text-1'))}
                      </div>
                      <div className="shrink-0" style={{ width: imgW, paddingLeft: '0.5rem', paddingRight: '1rem' }}>
                        <div className="relative w-full">
                          <div ref={artifactImageOverlayRef} className="absolute top-1/2 left-0 w-full z-20" style={{ transform: 'translateY(-50%)' }}>
                            {renderer(bp('uuid-artifact-image-1', { noAspect: true }))}
                          </div>
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="shrink-0" style={{ width: imgW, paddingLeft: '1rem', paddingRight: '0.5rem' }}>
                        <div className="relative w-full">
                          <div ref={artifactImageOverlayRef} className="absolute top-1/2 left-0 w-full z-20" style={{ transform: 'translateY(-50%)' }}>
                            {renderer(bp('uuid-artifact-image-1', { noAspect: true }))}
                          </div>
                        </div>
                      </div>
                      <div ref={artifactTextRef} className="shrink-0 py-4 @min-[443px]:py-6" style={{ width: textW, paddingRight: isMobile ? '1rem' : '1.5rem' }}>
                        {!isDecorVariant && !isTemplate2 && !isTemplate6 && (
                          <div ref={showConnector ? artifactTitle1Ref : undefined}>{renderer(bp('uuid-artifact-title-1'))}</div>
                        )}
                        {renderer(bp('uuid-artifact-text-1'))}
                      </div>
                    </>
                  )}
                </div>
              </div>
            )
          })()}
        </div>
      )}

      {/* ── 精舍剪影區 ── */}
      {blocks.find(b => b.id === 'uuid-section-3') && !isSilhouetteEmpty && (
        <div ref={silhouetteSectionRef} className={`w-full py-8 px-6 ${settings.silhouetteNumber ? 'space-y-3' : 'space-y-6'}`}>
          {isTemplate4 ? (
            <CenterOverTarget selfRef={silhouetteTitleRef} targetRef={silhouetteFirstItemRef} align="left" watch={isMobile}>
              <SectionTitle value={blocks.find(b => b.id === 'uuid-section-3')?.data.value} titleStyle={silhouetteTitleStyle} isMobile={isMobile} barColor={titleBarColor} />
            </CenterOverTarget>
          ) : (
            <div ref={silhouetteTitleRef} style={
              (silhouetteTitleStyle === 'underline-left' || silhouetteTitleStyle === 'underline-right')
                ? { marginLeft: '-1.5rem', marginRight: '-1.5rem' }
                : isTemplate5 ? { marginLeft: '65%' }
                : undefined
            }><SectionTitle value={blocks.find(b => b.id === 'uuid-section-3')?.data.value} titleStyle={silhouetteTitleStyle} isMobile={isMobile} barColor={titleBarColor} /></div>
          )}

          {isTemplate5 && verticalConnector('right', '35%', 48, { marginTop: 0 }, 0)}
          {isTemplate4 && (
            <AlignedFixedConnector containerRef={silhouetteSectionRef} alignRef={silhouetteFirstItemRef} align="left" height={isMobile ? 48 : 64} watch={isMobile} />
          )}

          {silhouetteV.layout === null ? (() => {
            const isEditing = !!extraBlockProps?.('uuid-silhouette-image-1')?.isEditing
            const globalOnUpdate = extraBlockProps?.('uuid-silhouette-image-1')?.onUpdate

            // ── 模板二：非對稱錯位 ──────────────────────────────────────────
            // [回退用] 原本固定高度裁切寫法，若要改回 cover 裁切，換回下面這版：
            // if (silhouetteV.id === 6) {
            //   const cell = (id, h, ratio) => (
            //     <div key={id} style={{ position: 'relative', height: h, borderRadius: '8px', overflow: 'hidden' }}>
            //       <SilhouetteCell
            //         block={blocks.find(b => b.id === id)}
            //         isEditing={isEditing}
            //         onUpdate={globalOnUpdate ? (val) => globalOnUpdate(id, val) : undefined}
            //         ratioHint={ratio}
            //         emptyColor={themeColor}
            //       />
            //     </div>
            //   )
            //   return (
            //     <div className="space-y-1">
            //       <div style={{ display: 'flex', gap: '0.5rem' }}>
            //         <div style={{ flex: 4 }}>{cell('uuid-silhouette-image-1', '180px', '建議上傳 4:3 比例')}</div>
            //         <div style={{ flex: 6 }}>{cell('uuid-silhouette-image-2', '180px', '建議上傳 4:3 比例')}</div>
            //       </div>
            //       <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start' }}>
            //         <div style={{ flex: 6 }}>{cell('uuid-silhouette-image-3', '240px', '建議上傳 1:1 比例')}</div>
            //         <div style={{ flex: 4 }}>{cell('uuid-silhouette-image-4', '180px', '建議上傳 4:3 比例')}</div>
            //       </div>
            //     </div>
            //   )
            // }
            if (silhouetteV.id === 6) {
              const cell = (id, ratio, h) => (
                <div key={id} style={{ position: 'relative', height: h, borderRadius: '8px', overflow: 'hidden' }}>
                  <SilhouetteCell
                    block={blocks.find(b => b.id === id)}
                    isEditing={isEditing}
                    onUpdate={globalOnUpdate ? (val) => globalOnUpdate(id, val) : undefined}
                    ratioHint={ratio}
                    emptyColor={themeColor}
                    onOpenLightbox={isReadOnlyView ? openLightbox : undefined}
                    noAspect={!h}
                  />
                </div>
              )
              return (
                <div className="space-y-1">
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <div style={{ flex: 4 }}>{cell('uuid-silhouette-image-1', '建議上傳 4:3 比例', '180px')}</div>
                    <div style={{ flex: 6 }}>{cell('uuid-silhouette-image-2', '建議上傳 4:3 比例', '180px')}</div>
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start' }}>
                    <div style={{ flex: 6 }}>{cell('uuid-silhouette-image-3', '建議上傳 1:1 比例')}</div>
                    <div style={{ flex: 4 }}>{cell('uuid-silhouette-image-4', '建議上傳 4:3 比例')}</div>
                  </div>
                </div>
              )
            }

            // ── 模板五：圖文並排靠右，彈性張數，圖片依原始比例 ─────────────
            if (silhouetteV.id === 8) {
              const pairs = Array.from({ length: 10 }, (_, i) => {
                const imgId = `uuid-silhouette-image-${i + 1}`
                const txtId = `uuid-silhouette-text-${i + 1}`
                const imgBlock = blocks.find(b => b.id === imgId)
                if (!imgBlock?.data?.url && !isEditing) return null
                if (!imgBlock) return null
                const txtBlock = blocks.find(b => b.id === txtId)
                const hasImg = !!imgBlock?.data?.url
                return (
                  <div key={imgId} style={{ display: 'flex', alignItems: 'flex-start', gap: '0', justifyContent: 'flex-end' }}>
                    <div style={{ flex: hasImg ? '0 1 auto' : '0 0 55%', maxWidth: '55%' }}>
                      {renderer(bp(imgId, { noAspect: true, enableFullImage: true }))}
                    </div>
                    <div style={{ flex: '0 0 35%', maxWidth: '35%', paddingTop: '0.25rem' }}>
                      {txtBlock && renderer(bp(txtId))}
                    </div>
                  </div>
                )
              }).filter(Boolean)

              return <div className="space-y-2" style={{ marginTop: 0 }}>{pairs}</div>
            }

            // ── 模板六：三欄同列 + 第四張靠右 ──────────────────────────────
            // [回退用] 原本固定 aspectRatio 裁切寫法，若要改回 cover 裁切，換回下面這版：
            // if (silhouetteV.id === 9) {
            //   const cell = (id, aspect, ratio) => (
            //     <div key={id} style={{ position: 'relative', aspectRatio: aspect, borderRadius: '8px', overflow: 'hidden' }}>
            //       <SilhouetteCell
            //         block={blocks.find(b => b.id === id)}
            //         isEditing={isEditing}
            //         onUpdate={globalOnUpdate ? (val) => globalOnUpdate(id, val) : undefined}
            //         ratioHint={ratio}
            //         emptyColor={themeColor}
            //       />
            //     </div>
            //   )
            //   return (
            //     <div className="space-y-0.5">
            //       <div style={{ display: 'flex', gap: '2px', alignItems: 'stretch' }}>
            //         <div style={{ flex: 4, display: 'flex', flexDirection: 'column', gap: '2px' }}>
            //           <div style={{ width: '65%', aspectRatio: '4/3', position: 'relative', borderRadius: '8px', overflow: 'hidden', marginLeft: 'auto' }}>
            //             <SilhouetteCell block={blocks.find(b => b.id === 'uuid-silhouette-image-1')} isEditing={isEditing} onUpdate={globalOnUpdate ? (val) => globalOnUpdate('uuid-silhouette-image-1', val) : undefined} ratioHint="建議上傳 4:3 比例" small emptyColor={themeColor} />
            //           </div>
            //           {cell('uuid-silhouette-image-2', '4/3', '建議上傳 4:3 比例')}
            //         </div>
            //         <div style={{ flex: 6, position: 'relative', borderRadius: '8px', overflow: 'hidden' }}>
            //           <SilhouetteCell
            //             block={blocks.find(b => b.id === 'uuid-silhouette-image-3')}
            //             isEditing={isEditing}
            //             onUpdate={globalOnUpdate ? (val) => globalOnUpdate('uuid-silhouette-image-3', val) : undefined}
            //             ratioHint="建議上傳 2:3 比例"
            //             emptyColor={themeColor}
            //           />
            //         </div>
            //       </div>
            //       <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            //         <div style={{ flex: '0 0 60%', aspectRatio: '16/9', position: 'relative', borderRadius: '8px', overflow: 'hidden' }}>
            //           <SilhouetteCell
            //             block={blocks.find(b => b.id === 'uuid-silhouette-image-4')}
            //             isEditing={isEditing}
            //             onUpdate={globalOnUpdate ? (val) => globalOnUpdate('uuid-silhouette-image-4', val) : undefined}
            //             ratioHint="建議上傳 16:9 比例"
            //             emptyColor={themeColor}
            //           />
            //         </div>
            //       </div>
            //     </div>
            //   )
            // }
            if (silhouetteV.id === 9) {
              const cell = (id, ratio) => (
                <div key={id} style={{ position: 'relative', borderRadius: '8px', overflow: 'hidden' }}>
                  <SilhouetteCell
                    block={blocks.find(b => b.id === id)}
                    isEditing={isEditing}
                    onUpdate={globalOnUpdate ? (val) => globalOnUpdate(id, val) : undefined}
                    ratioHint={ratio}
                    emptyColor={themeColor}
                    onOpenLightbox={isReadOnlyView ? openLightbox : undefined}
                    noAspect
                  />
                </div>
              )
              return (
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start' }}>
                  <div style={{ flex: '0 1 auto', maxWidth: '50%', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    {cell('uuid-silhouette-image-1', '建議上傳 4:3 比例')}
                    {cell('uuid-silhouette-image-2', '建議上傳 4:3 比例')}
                  </div>
                  <div style={{ flex: '0 1 auto', maxWidth: '50%', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    {cell('uuid-silhouette-image-3', '建議上傳 2:3 比例')}
                    {cell('uuid-silhouette-image-4', '建議上傳 16:9 比例')}
                  </div>
                </div>
              )
            }

            // ── 模板七：三欄裝飾錯位 ─────────────────────────────────────────
            // [回退用] 原本 absolute 疊圖 + paddingBottom 固定比例裁切寫法，若要改回 cover 裁切，換回下面這版：
            // if (silhouetteV.id === 10) {
            //   const imgCell = (id) => {
            //     const block = blocks.find(b => b.id === id)
            //     return (
            //       <div style={{ position: 'absolute', inset: 0, borderRadius: '4px', overflow: 'hidden' }}>
            //         <SilhouetteCell
            //           block={block}
            //           isEditing={isEditing}
            //           onUpdate={globalOnUpdate ? (val) => globalOnUpdate(id, val) : undefined}
            //           ratioHint=""
            //           emptyColor={themeColor}
            //         />
            //       </div>
            //     )
            //   }
            //   const decor = (color) => (
            //     <div style={{ position: 'absolute', inset: 0, borderRadius: '4px', background: color }} />
            //   )
            //   const cell = (content, flex) => (
            //     <div style={{ flex: `0 0 ${flex}`, position: 'relative', paddingBottom: '30%' }}>
            //       {content}
            //     </div>
            //   )
            //   return (
            //     <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
            //       <div style={{ display: 'flex', gap: '2px' }}>
            //         {cell(decor('#C4D4B9'), '22%')}
            //         {cell(imgCell('uuid-silhouette-image-1'), '62%')}
            //         {cell(decor('#DDE6D7'), '16%')}
            //       </div>
            //       <div style={{ display: 'flex', gap: '2px' }}>
            //         {cell(decor('#ADBFA2'), '11%')}
            //         {cell(imgCell('uuid-silhouette-image-2'), '45%')}
            //         {cell(imgCell('uuid-silhouette-image-3'), '44%')}
            //       </div>
            //       <div style={{ display: 'flex', gap: '2px' }}>
            //         {cell(decor('#DBDBDB'), '29%')}
            //         {cell(imgCell('uuid-silhouette-image-4'), '45%')}
            //         {cell(decor('#C1CFB8'), '26%')}
            //       </div>
            //     </div>
            //   )
            // }
            if (silhouetteV.id === 10) {
              const imgCell = (id) => {
                const block = blocks.find(b => b.id === id)
                return (
                  <div style={{ borderRadius: '4px', overflow: 'hidden' }}>
                    <SilhouetteCell
                      block={block}
                      isEditing={isEditing}
                      onUpdate={globalOnUpdate ? (val) => globalOnUpdate(id, val) : undefined}
                      ratioHint=""
                      emptyColor={themeColor}
                      onOpenLightbox={isReadOnlyView ? openLightbox : undefined}
                      noAspect
                    />
                  </div>
                )
              }
              const decor = (color) => (
                <div style={{ position: 'absolute', inset: 0, borderRadius: '4px', background: color }} />
              )
              // 裝飾色塊沒有內容可撐開高度，仍用 paddingBottom 固定比例；
              // 圖片欄改成一般流內排版，讓 noAspect 的 height:auto 生效
              const decorCell = (color, flex) => (
                <div style={{ flex: `0 0 ${flex}`, position: 'relative', paddingBottom: '30%' }}>
                  {decor(color)}
                </div>
              )
              const imgFlexCell = (id, flex) => (
                <div style={{ flex: `0 0 ${flex}` }}>
                  {imgCell(id)}
                </div>
              )
              return (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                  <div style={{ display: 'flex', gap: '2px', alignItems: 'flex-start' }}>
                    {decorCell('#C4D4B9', '22%')}
                    {imgFlexCell('uuid-silhouette-image-1', '62%')}
                    {decorCell('#DDE6D7', '16%')}
                  </div>
                  <div style={{ display: 'flex', gap: '2px', alignItems: 'flex-start' }}>
                    {decorCell('#ADBFA2', '11%')}
                    {imgFlexCell('uuid-silhouette-image-2', '45%')}
                    {imgFlexCell('uuid-silhouette-image-3', '44%')}
                  </div>
                  <div style={{ display: 'flex', gap: '2px', alignItems: 'flex-start' }}>
                    {decorCell('#DBDBDB', '29%')}
                    {imgFlexCell('uuid-silhouette-image-4', '45%')}
                    {decorCell('#C1CFB8', '26%')}
                  </div>
                </div>
              )
            }

            return null
          })() : (
            [1, 2, 3, 4].map(n => {
              const imgId = `uuid-silhouette-image-${n}`
              const txtId = `uuid-silhouette-text-${n}`
              const imgBlock = blocks.find(b => b.id === imgId)
              if (!imgBlock) return null
              const txtBlock = blocks.find(b => b.id === txtId)
              const lay = silhouetteV.layout[n - 1]
              const silhouetteText = settings.silhouetteText

              if (silhouetteText === 'right' && txtBlock) {
                return (
                  <div key={imgId} className="w-full px-4">
                    <div style={{ width: '80%', marginLeft: lay.ml, marginRight: lay.mr, display: 'flex', alignItems: 'flex-start', gap: '1rem' }}>
                      <div style={{ flex: '0 0 60%' }}>{renderer(bp(imgId))}</div>
                      <div style={{ flex: 1, paddingTop: '0.5rem' }}>{renderer(bp(txtId))}</div>
                    </div>
                  </div>
                )
              }

              const showNumber = !!settings.silhouetteNumber
              const numLabel = String(n).padStart(2, '0')

              return (
                <div key={imgId} className="w-full px-4">
                  <div ref={n === 1 ? silhouetteFirstItemRef : undefined} style={{ width: showNumber ? '86%' : '74%', marginLeft: lay.ml, marginRight: lay.mr, display: 'flex', alignItems: 'flex-start', gap: '0.5rem' }}>
                    {showNumber && (
                      <div style={{ flexShrink: 0, width: isMobile ? '2.5rem' : '3.5rem', color: '#b39029', fontSize: isMobile ? '2rem' : '3rem', fontWeight: 'bold', fontFamily: 'Times New Roman', lineHeight: 1, paddingTop: '0.25rem' }}>
                        {numLabel}
                      </div>
                    )}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      {renderer(bp(imgId, { noAspect: true }))}
                      {silhouetteText === 'below' && txtBlock && (
                        <div className={`mt-2 ${showNumber ? '-mb-4' : ''}`}>
                          {renderer(bp(txtId))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )
            })
          )}
        </div>
      )}

      {isReadOnlyView && lightboxIndex !== null && (
        <Lightbox
          images={galleryImages}
          index={lightboxIndex}
          onIndexChange={setLightboxIndex}
          onClose={() => setLightboxIndex(null)}
        />
      )}

    </div>
  )
}

export function TransmissionView({ blocks, theme = DEFAULT_THEME, settings = DEFAULT_TEMPLATE_SETTINGS, isMobile }) {
  return <TransmissionLayout blocks={blocks} theme={theme} settings={settings} isMobile={isMobile} />
}
